import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import moment from "moment";
import ReactLoading from "react-loading";
import ReactPaginate from "react-paginate";
import ReusableButton from "../../../CommonComponents/Button/Button";
import { API } from "../../../Networking/API";
import { OngoingDriveSelect } from "./OngoingDriveSelect";
import { interviewBelongsToDrive } from "./useOngoingDriveFilter";

const PAGE_SIZE = 10;
const INTERVIEW_FETCH_PAGE_SIZE = 50;

const INTERVIEW_STATUS_CONFIG = {
  in_progress: { label: "In Progress", color: "#1565c0", bg: "#e3f2fd" },
  completed: { label: "Completed", color: "#2e7d32", bg: "#e8f5e9" },
  terminated: { label: "Terminated", color: "#c62828", bg: "#ffebee" },
  abandoned: { label: "Abandoned", color: "#ef6c00", bg: "#fff3e0" },
  pending: { label: "Pending", color: "#616161", bg: "#f5f5f5" },
};

const STAT_CARDS = [
  { key: "total", label: "Total Interviews", color: "#00728E" },
  { key: "completed", label: "Completed", color: "#2e7d32" },
  { key: "terminated", label: "Terminated", color: "#c62828" },
  { key: "abandoned", label: "Abandoned", color: "#ef6c00" },
  { key: "pending_appeals", label: "Pending Appeals", color: "#7c3aed" },
];

const FILTER_OPTIONS = ["all", "terminated", "abandoned", "completed"];

function scoreColor(score) {
  if (score == null || Number.isNaN(Number(score))) return "#757575";
  const n = Number(score);
  if (n >= 7) return "#2e7d32";
  if (n >= 5) return "#ef6c00";
  return "#c62828";
}

function QAItem({ index, question }) {
  const [qaOpen, setQaOpen] = useState(false);
  const score = question.score ?? null;
  const commScore = question.communication_score ?? null;

  return (
    <div className="ai-admin-qa-item">
      <button
        type="button"
        className="ai-admin-qa-header"
        onClick={(e) => {
          e.stopPropagation();
          setQaOpen((v) => !v);
        }}
      >
        <span className="ai-admin-qa-num">Q{index}</span>
        <span className="ai-admin-qa-qtext">{question.question_text}</span>
        <span className="ai-admin-qa-scores">
          {score != null && (
            <span style={{ color: scoreColor(score) }}>
              {Number(score).toFixed(1)}/10
            </span>
          )}
          {commScore != null && (
            <span className="ai-admin-qa-comm" title="Communication score">
              Comm {Number(commScore).toFixed(1)}
            </span>
          )}
        </span>
        {qaOpen ? (
          <KeyboardArrowDownIcon fontSize="small" />
        ) : (
          <KeyboardArrowRightIcon fontSize="small" />
        )}
      </button>
      {qaOpen && (
        <div className="ai-admin-qa-body">
          {question.question_type && (
            <Chip
              label={question.question_type.replace(/_/g, " ")}
              size="small"
              sx={{ mb: 1, textTransform: "capitalize", fontSize: "0.72rem" }}
            />
          )}
          <div className="ai-admin-qa-section">
            <div className="ai-admin-qa-label">Answer</div>
            <p>{question.user_answer?.trim() || "—"}</p>
          </div>
          {question.ai_feedback && (
            <div className="ai-admin-qa-section">
              <div className="ai-admin-qa-label">AI Feedback</div>
              <p>{question.ai_feedback}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusChip({ status }) {
  const key = status?.toLowerCase();
  const config =
    INTERVIEW_STATUS_CONFIG[key] || INTERVIEW_STATUS_CONFIG.pending;
  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        backgroundColor: config.bg,
        color: config.color,
        fontWeight: 600,
        fontSize: "0.75rem",
      }}
    />
  );
}

function InterviewRow({ interview }) {
  const [open, setOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const screenshotCount =
    interview.security_events?.filter((e) => e.screenshot)?.length || 0;
  const sortedQuestions = useMemo(
    () =>
      [...(interview.questions || [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
      ),
    [interview.questions]
  );

  return (
    <>
      <TableRow
        hover
        className={open ? "ai-admin-row-open" : ""}
        onClick={() => setOpen((v) => !v)}
        sx={{ cursor: "pointer" }}
      >
        <TableCell>
          <IconButton size="small" aria-label="expand row">
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <div style={{ fontWeight: 600 }}>{interview.user_name || "—"}</div>
          <div style={{ color: "#757575", fontSize: "0.8rem" }}>
            {interview.user_email}
          </div>
        </TableCell>
        <TableCell>{interview.job_role}</TableCell>
        <TableCell>
          <StatusChip status={interview.status} />
        </TableCell>
        <TableCell align="center">
          {interview.overall_score != null
            ? `${Number(interview.overall_score).toFixed(1)}/10`
            : "—"}
        </TableCell>
        <TableCell align="center">
          {interview.warnings_count > 0 ? (
            <span style={{ color: "#c62828", fontWeight: 600 }}>
              {interview.warnings_count}
            </span>
          ) : (
            "0"
          )}
        </TableCell>
        <TableCell align="center">
          {screenshotCount > 0 ? (
            <span style={{ color: "#ef6c00", fontWeight: 600 }}>
              <PhotoCameraOutlinedIcon
                sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }}
              />
              {screenshotCount}
            </span>
          ) : (
            "—"
          )}
        </TableCell>
        <TableCell>
          {moment(interview.created_at).format("DD MMM YYYY")}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box className="ai-admin-expand-panel">
              {(interview.tech_stack ||
                interview.overall_communication_score != null) && (
                <div className="ai-admin-interview-meta">
                  {interview.tech_stack && (
                    <span>
                      <strong>Tech stack:</strong> {interview.tech_stack}
                    </span>
                  )}
                  {interview.overall_communication_score != null && (
                    <span>
                      <strong>Communication:</strong>{" "}
                      {Number(interview.overall_communication_score).toFixed(1)}
                      /10
                    </span>
                  )}
                </div>
              )}

              <div className="ai-admin-security-title">Security Events</div>
              {!interview.security_events?.length ? (
                <div className="ai-admin-security-empty">No security events</div>
              ) : (
                <>
                  <div className="ai-admin-screenshot-grid">
                    {interview.security_events.map((ev) => (
                      <div key={ev.id} className="ai-admin-screenshot-card">
                        {ev.screenshot ? (
                          <img
                            src={ev.screenshot}
                            alt={ev.event_type}
                            onClick={(e) => {
                              e.stopPropagation();
                              setLightboxSrc(ev.screenshot);
                            }}
                          />
                        ) : (
                          <div className="ai-admin-screenshot-placeholder">
                            No image
                          </div>
                        )}
                        <div className="ai-admin-screenshot-meta">
                          <div className="ai-admin-event-type">
                            {ev.event_type?.replace(/_/g, " ")}
                          </div>
                          <div className="ai-admin-event-time">
                            {moment(ev.created_at).format("HH:mm:ss")}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {interview.security_events.some((ev) => ev.audio_clip) && (
                    <div className="ai-admin-audio-section">
                      <div className="ai-admin-security-title">
                        Captured Audio Clips
                      </div>
                      {interview.security_events
                        .filter((ev) => ev.audio_clip)
                        .map((ev) => (
                          <div key={`audio-${ev.id}`} className="ai-admin-audio-row">
                            <div>
                              <div className="ai-admin-event-type">
                                {ev.event_type?.replace(/_/g, " ")}
                              </div>
                              <div className="ai-admin-event-time">
                                {moment(ev.created_at).format("HH:mm:ss")}
                              </div>
                            </div>
                            <audio controls src={ev.audio_clip} />
                          </div>
                        ))}
                    </div>
                  )}
                </>
              )}

              <div className="ai-admin-qa-section-wrap">
                <div className="ai-admin-security-title">
                  Questions &amp; Answers
                  {sortedQuestions.length > 0 && (
                    <span className="ai-admin-qa-count">
                      ({sortedQuestions.length})
                    </span>
                  )}
                </div>
                {sortedQuestions.length === 0 ? (
                  <div className="ai-admin-security-empty">
                    No questions recorded
                  </div>
                ) : (
                  <div className="ai-admin-qa-list">
                    {sortedQuestions.map((q, i) => (
                      <QAItem key={q.id ?? i} index={i + 1} question={q} />
                    ))}
                  </div>
                )}
              </div>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <Dialog
        open={!!lightboxSrc}
        onClose={() => setLightboxSrc(null)}
        maxWidth="md"
        onClick={() => setLightboxSrc(null)}
      >
        <DialogContent sx={{ p: 1, position: "relative" }}>
          <IconButton
            onClick={() => setLightboxSrc(null)}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          {lightboxSrc && (
            <img
              src={lightboxSrc}
              alt="Security screenshot"
              style={{ width: "100%", borderRadius: 8 }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function AppealCard({ appeal, onReview, setNotify }) {
  const [loading, setLoading] = useState(false);

  const handleReview = async (action) => {
    setLoading(true);
    const result = await API.reviewAiInterviewAppeal(appeal.id, action);
    if (result?.response?.data?.status === true) {
      setNotify({
        isOpen: true,
        message: `Appeal ${action === "approve" ? "approved" : "denied"} successfully`,
        type: "success",
      });
      onReview();
    } else {
      setNotify({
        isOpen: true,
        message:
          result?.response?.data?.message ||
          result?.error?.response?.data?.message ||
          "Failed to review appeal",
        type: "error",
      });
    }
    setLoading(false);
  };

  return (
    <Paper className="ai-admin-appeal-card" elevation={0}>
      <div className="ai-admin-appeal-header">
        <div>
          <div style={{ fontWeight: 600 }}>{appeal.full_name}</div>
          <div style={{ fontSize: "0.8rem", color: "#757575" }}>{appeal.email}</div>
        </div>
        <div style={{ fontSize: "0.78rem", color: "#757575" }}>
          {moment(appeal.appeal_submitted_at).format("DD MMM YYYY, HH:mm")}
        </div>
      </div>
      <div style={{ fontSize: "0.82rem", color: "#757575", marginBottom: 8 }}>
        Abandoned{" "}
        <strong style={{ color: "#c62828" }}>{appeal.abandoned_count}x</strong>
      </div>
      <p className="ai-admin-appeal-reason">{appeal.appeal_reason}</p>
      <div className="ai-admin-appeal-actions">
        <ReusableButton
          text="Approve"
          disabled={loading}
          onClick={() => handleReview("approve")}
          style={{
            backgroundColor: "#2e7d32",
            textTransform: "capitalize",
            fontSize: "0.85rem",
            padding: "6px 14px",
          }}
        />
        <ReusableButton
          text="Deny"
          disabled={loading}
          onClick={() => handleReview("deny")}
          style={{
            backgroundColor: "#c62828",
            textTransform: "capitalize",
            fontSize: "0.85rem",
            padding: "6px 14px",
          }}
        />
      </div>
    </Paper>
  );
}

async function fetchAllAdminInterviews(statusFilter) {
  let pageNum = 1;
  let totalPages = 1;
  const allItems = [];

  while (pageNum <= totalPages) {
    const result = await API.getAiInterviewAdminInterviews(
      pageNum,
      INTERVIEW_FETCH_PAGE_SIZE,
      statusFilter
    );
    if (!result?.response?.data?.status) {
      return { ok: false, items: [], error: result };
    }
    const payload = result.response.data.data || {};
    allItems.push(...(Array.isArray(payload.items) ? payload.items : []));
    totalPages = payload.total_pages || 1;
    pageNum += 1;
  }

  return { ok: true, items: allItems };
}

export function AdminAiInterviewDashboardTab({ setNotify, driveFilter }) {
  const {
    drives,
    selectedDriveId,
    setSelectedDriveId,
    selectedDrive,
    driveEmails,
    loadingDrives,
    loadingCandidates,
    hasDrives,
    formatDriveStatus,
  } = driveFilter;

  const [sectionTab, setSectionTab] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    terminated: 0,
    abandoned: 0,
    pending_appeals: 0,
  });
  const [interviews, setInterviews] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [listPage, setListPage] = useState(1);
  const [lastUpdated, setLastUpdated] = useState(null);
  const pollRef = useRef(null);

  const fetchStatsAndAppeals = useCallback(async () => {
    const [statsRes, appealsRes] = await Promise.all([
      API.getAiInterviewAdminStats(),
      API.getAiInterviewAdminAppeals(),
    ]);

    if (statsRes?.response?.data?.status) {
      setStats(statsRes.response.data.data || {});
    }
    if (appealsRes?.response?.data?.status) {
      setAppeals(
        Array.isArray(appealsRes.response.data.data)
          ? appealsRes.response.data.data
          : []
      );
    }
    setLastUpdated(new Date());
  }, []);

  const fetchInterviews = useCallback(
    async (statusFilter = filter) => {
      setLoading(true);
      const { ok, items, error } = await fetchAllAdminInterviews(statusFilter);

      if (ok) {
        setInterviews(items);
        setListPage(1);
      } else {
        setInterviews([]);
        setNotify({
          isOpen: true,
          message:
            error?.error?.response?.data?.message ||
            "Failed to load AI interview dashboard data",
          type: "error",
        });
      }
      setLoading(false);
    },
    [filter, setNotify]
  );

  useEffect(() => {
    fetchStatsAndAppeals();
  }, [fetchStatsAndAppeals]);

  useEffect(() => {
    if (loadingDrives || loadingCandidates) return;
    if (!hasDrives) {
      setInterviews([]);
      setLoading(false);
      return;
    }
    fetchInterviews(filter);
  }, [filter, loadingDrives, loadingCandidates, hasDrives, fetchInterviews]);

  useEffect(() => {
    pollRef.current = setInterval(() => {
      fetchStatsAndAppeals();
      if (hasDrives && !loadingDrives && !loadingCandidates) {
        fetchInterviews(filter);
      }
    }, 2 * 60 * 1000);

    return () => clearInterval(pollRef.current);
  }, [
    filter,
    hasDrives,
    loadingDrives,
    loadingCandidates,
    fetchStatsAndAppeals,
    fetchInterviews,
  ]);

  const handleFilterChange = (f) => {
    setFilter(f);
    setSearch("");
    setListPage(1);
  };

  const handlePageChange = ({ selected }) => {
    setListPage(selected + 1);
  };

  const driveFilteredInterviews = useMemo(() => {
    if (!driveEmails.size) return [];
    return interviews.filter((iv) => interviewBelongsToDrive(iv, driveEmails));
  }, [interviews, driveEmails]);

  const filteredInterviews = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return driveFilteredInterviews;
    return driveFilteredInterviews.filter(
      (iv) =>
        iv.user_name?.toLowerCase().includes(q) ||
        iv.user_email?.toLowerCase().includes(q)
    );
  }, [driveFilteredInterviews, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredInterviews.length / PAGE_SIZE)
  );

  const paginatedInterviews = useMemo(() => {
    const start = (listPage - 1) * PAGE_SIZE;
    return filteredInterviews.slice(start, start + PAGE_SIZE);
  }, [filteredInterviews, listPage]);

  useEffect(() => {
    setListPage(1);
  }, [search, filter, selectedDriveId]);

  useEffect(() => {
    if (listPage > totalPages) {
      setListPage(totalPages);
    }
  }, [listPage, totalPages]);

  const refreshAll = () => {
    fetchStatsAndAppeals();
    fetchInterviews(filter);
  };

  const tableLoading =
    loading || loadingDrives || loadingCandidates || !hasDrives;

  return (
    <Box className="ai-admin-dashboard">
      {lastUpdated && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <span style={{ fontSize: "0.75rem", color: "#757575" }}>
            Auto-refreshes every 2 min &nbsp;·&nbsp; Last updated:{" "}
            {lastUpdated.toLocaleTimeString()}
          </span>
        </Box>
      )}
      <Grid container spacing={2} className="ai-admin-stats-grid">
        {STAT_CARDS.map((card) => (
          <Grid item xs={6} sm={4} md key={card.key}>
            <Paper className="ai-admin-stat-card" elevation={0}>
              <div
                className="ai-admin-stat-value"
                style={{ color: card.color }}
              >
                {stats[card.key] ?? 0}
              </div>
              <div className="ai-admin-stat-label">{card.label}</div>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Tabs
        value={sectionTab}
        onChange={(_, v) => setSectionTab(v)}
        className="ai-admin-section-tabs"
      >
        <Tab label="Interviews" />
        <Tab
          label={
            stats.pending_appeals > 0
              ? `Pending Appeals (${stats.pending_appeals})`
              : "Pending Appeals"
          }
        />
      </Tabs>

      {tableLoading && sectionTab === 0 ? (
        <Box className="ai-interview-loading">
          <ReactLoading
            type="spinningBubbles"
            color="#00728E"
            height={80}
            width={80}
          />
        </Box>
      ) : sectionTab === 0 ? (
        <>
          <Box className="ai-admin-toolbar">
            <Box className="ai-admin-toolbar-left">
              <TextField
                size="small"
                placeholder="Search by name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 260, backgroundColor: "#fff" }}
              />
              <OngoingDriveSelect
                drives={drives}
                selectedDriveId={selectedDriveId}
                onChange={setSelectedDriveId}
                loading={loadingDrives || loadingCandidates}
                formatDriveStatus={formatDriveStatus}
              />
            </Box>
            <Box className="ai-admin-filter-bar">
              {FILTER_OPTIONS.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`ai-admin-filter-btn ${
                    filter === f ? "ai-admin-filter-btn--active" : ""
                  }`}
                  onClick={() => handleFilterChange(f)}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </Box>
          </Box>

          {selectedDrive && (
            <Box sx={{ mb: 1, fontSize: "0.8rem", color: "#757575" }}>
              Showing interviews for candidates in{" "}
              <strong>
                {selectedDrive.name}
                {selectedDrive.runningStatus
                  ? ` (${formatDriveStatus(selectedDrive.runningStatus)})`
                  : ""}
              </strong>
              {filteredInterviews.length > 0 &&
                ` (${filteredInterviews.length} interview${filteredInterviews.length === 1 ? "" : "s"})`}
            </Box>
          )}

          <TableContainer component={Paper} elevation={0} className="ai-admin-table-wrap">
            <Table size="small">
              <TableHead>
                <TableRow className="ai-admin-table-head">
                  <TableCell width={48} />
                  <TableCell>Candidate</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Score</TableCell>
                  <TableCell align="center">Warnings</TableCell>
                  <TableCell align="center">Screenshots</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!hasDrives ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      No drives found
                    </TableCell>
                  </TableRow>
                ) : filteredInterviews.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      No interviews found for this drive
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedInterviews.map((iv) => (
                    <InterviewRow key={iv.id} interview={iv} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {hasDrives && totalPages > 1 && (
            <Box className="ai-admin-pagination">
              <ReactPaginate
                breakLabel="..."
                nextLabel="›"
                previousLabel="‹"
                onPageChange={handlePageChange}
                pageRangeDisplayed={3}
                marginPagesDisplayed={1}
                pageCount={totalPages}
                forcePage={listPage - 1}
                containerClassName="pagination"
                activeClassName="active"
              />
            </Box>
          )}
        </>
      ) : (
        <Box className="ai-admin-appeals-grid">
          {appeals.length === 0 ? (
            <Box className="ai-interview-empty" sx={{ gridColumn: "1 / -1" }}>
              No pending appeals
            </Box>
          ) : (
            appeals.map((appeal) => (
              <AppealCard
                key={appeal.id}
                appeal={appeal}
                onReview={refreshAll}
                setNotify={setNotify}
              />
            ))
          )}
        </Box>
      )}
    </Box>
  );
}

export default AdminAiInterviewDashboardTab;
